import React from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { getExtension } from '@/hooks/useUpload'
import {
  ImFileEmpty,
  ImFileExcel,
  ImFileMusic,
  ImFilePdf,
  ImFileText,
  ImFileVideo,
  ImFileWord,
} from 'react-icons/im'
import { MdFileDownload } from 'react-icons/md'

import { Box, Flex, IconButton, Text } from '@chakra-ui/react'

interface FileThumbnailIProps {
  name: string
  isDownloading?: boolean
  // Card click. Falls back to onDownload when omitted
  onClick?: () => void
  // Omit to render the card without a download affordance
  onDownload?: () => void
  width?: number | string
}

const getFileIcon = (extension: string) => {
  switch (extension) {
    case 'doc':
    case 'docx':
      return <ImFileWord color='#2B579A' size='20px' />
    case 'csv':
      return <ImFileExcel color='#217346' size='20px' />
    case 'mp3':
    case 'wav':
      return <ImFileMusic color='#8A4FBE' size='20px' />
    case 'pdf':
      return <ImFilePdf color='#E33E25' size='20px' />
    case 'mp4':
    case 'mov':
    case 'mpg':
    case 'mpeg':
      return <ImFileVideo color='#4A5568' size='20px' />
    case 'txt':
      return <ImFileText color='#4A5568' size='20px' />
    default:
      return <ImFileEmpty color='#4A5568' size='20px' />
  }
}

export const FileThumbnail = ({
  name,
  isDownloading,
  onClick,
  onDownload,
  width = 230,
}: FileThumbnailIProps) => {
  const mode = useLightMode()
  const extension = getExtension(name).toLowerCase()
  const handleCardClick = onClick ?? onDownload
  const handleDownloadClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    onDownload?.()
  }

  return (
    <Flex
      _hover={{ shadow: 'md' }}
      alignItems='center'
      backgroundColor={`bg.card-elevated.${mode}`}
      borderColor={`border.subtle.${mode}`}
      borderRadius='8px'
      borderWidth='1px'
      color={`text.primary.${mode}`}
      cursor={handleCardClick ? 'pointer' : 'default'}
      data-testid='fileThumbnail'
      height={50}
      maxWidth='100%'
      minWidth={0}
      onClick={handleCardClick}
      padding='6px'
      width={width}
    >
      {getFileIcon(extension)}
      <Box
        flex='1'
        marginLeft='8px'
        overflow='hidden'
        textOverflow='ellipsis'
        whiteSpace='nowrap'
      >
        <Text fontSize='xs' fontWeight='medium' isTruncated>
          {name}
        </Text>
        <Text color={`text.secondary.${mode}`} fontSize='10px'>
          {extension ? extension.toUpperCase() : 'FILE'}
        </Text>
      </Box>
      {onDownload && (
        <IconButton
          aria-label={`Download ${name}`}
          color={`text.secondary.${mode}`}
          icon={<MdFileDownload />}
          isLoading={isDownloading}
          onClick={handleDownloadClick}
          size='sm'
          variant='ghost'
        />
      )}
    </Flex>
  )
}
